import {HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse} from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  catchError,
  Observable,
  shareReplay,
  switchMap,
  throwError
} from "rxjs";
import { environment } from "src/environments/environment";
import { AuthorizationServiceProvider } from "./authorization-service.provider";
import { DeviceIdService } from "./device-id.service";
import { MobileAuthorizationApiInterface, WebAuthService} from "./api/authorization.api";
import { PlatformDescriptor } from "src/app/utils/platform-descriptor";

@Injectable()
export class JwtInterceptorService  implements HttpInterceptor {
  private appEndpoint = environment.appEndpoint;
  private authServerEndpoint = environment.authEndpoint;
  private casaEndpoint = environment.casaEndpoint;

  constructor(private authServiceProv: AuthorizationServiceProvider,
    private deviceIdService: DeviceIdService) { }

  filter(req: HttpRequest<any>): boolean {
    if (req.url.startsWith(this.authServerEndpoint)) { return true; }
    if (req.url.startsWith(environment.videoEndpoint)) {return true; }
    if (req.url.startsWith(environment.lessonEndpoint)) {return true; }
    if (req.url.startsWith(environment.appEndpoint)) {return true; }
    if (req.url.startsWith(environment.eventsApiBase)) {return true; }
    return req.url.startsWith(this.casaEndpoint);
  }

  requiresDeviceId(req: HttpRequest<any>): boolean {
    return req.url.startsWith(environment.appEndpoint) && !req.url.endsWith("/api/mobile/v3/public/device-key-id");
  }

  isUnprotectedRequest(req: HttpRequest<any>): boolean {
    return req.url.startsWith(this.appEndpoint + '/api/mobile/v3/public/')
      || (req.url.startsWith(this.authServerEndpoint) &&
        (req.url.endsWith('/authorize') || req.url.endsWith("/token"))
      );
  }

  applyHeaders(auth: WebAuthService, next: HttpHandler, req: HttpRequest<any>): Observable<HttpEvent<any>> {
    if (!auth.isTokenValid() || !auth.getAccessToken()) {
      auth.clear();
      return next.handle(req);
    }
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const headers = { Authorization: `Bearer ${auth.getAccessToken()}`, Timezone: timezone};
    if (this.requiresDeviceId(req)) {
      return this.deviceIdService.getDeviceId().pipe(
        switchMap( deviceId => {
          headers['X-Device-Id'] = deviceId;
          const newRequest = req.clone({setHeaders: headers});
          return next.handle(newRequest);
        })
      );
    } else {
      return next.handle(req.clone({setHeaders: headers}))
    }
  }

  applyNativeHeaders(auth: MobileAuthorizationApiInterface, next: HttpHandler, req: HttpRequest<any>): Observable<HttpEvent<any>> {
    return auth.getAccessTokenObservable().pipe(
      switchMap(token => {
        if (!token) {
          return next.handle(req);
        }
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        const headers = { Authorization: `Bearer ${token}`, Timezone: timezone };
        if (this.requiresDeviceId(req)) {
          return this.deviceIdService.getDeviceId().pipe(
            switchMap(deviceId => {
              headers['X-Device-Id'] = deviceId;
              return next.handle(req.clone({setHeaders: headers}));
            })
          )
        }
        return next.handle(req.clone({setHeaders: headers}));
      })
    );
  }

  lastRefreshed = 0;
  lastRefreshShare: Observable<any>;
  tryRefreshToken(auth: WebAuthService, next: HttpHandler): Observable<any> {
    if (Date.now() - this.lastRefreshed < 2000) return this.lastRefreshShare;
    this.lastRefreshed = Date.now();
    this.lastRefreshShare = auth.refreshToken().pipe(shareReplay(1));
    return this.lastRefreshShare;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler):
    Observable<HttpEvent<any>> {

    if (!this.filter(req)) return next.handle(req);

    if(this.isUnprotectedRequest(req)) {
      if (this.requiresDeviceId(req)) {
        return this.deviceIdService.getDeviceId().pipe(
          switchMap( deviceId => {
            const newRequest = req.clone({setHeaders: {'X-Device-Id': deviceId}});
            return next.handle(newRequest);
          })
        )
      }
      return next.handle(req);
    }

    if (environment.platform.name === PlatformDescriptor.Android.name
      || environment.platform.name === PlatformDescriptor.iOS.name) {
      return this.authServiceProv.getMobile().pipe(
        switchMap(mobileAuth => this.applyNativeHeaders(mobileAuth, next, req))
      );
    }

    return this.authServiceProv.getCommonRoutines().pipe(
      switchMap(commonAuth => {
        if (commonAuth?.isNativeAuthorization()) {
          return this.authServiceProv.getMobile().pipe(
            switchMap(mobileAuth => this.applyNativeHeaders(mobileAuth, next, req))
          );
        }
        return this.authServiceProv.getOnWebAuthorization().pipe(
          switchMap(auth => {
            if (!auth) {
              return this.authServiceProv.getMobile().pipe(
                switchMap(mobileAuth => this.applyNativeHeaders(mobileAuth, next, req))
              );
            }
            return this.applyHeaders(auth, next, req).pipe(
              catchError(error => {
                // handle 401 responses only  - requires a refresh token
                if (!(error instanceof HttpErrorResponse) || error.status != 401) return throwError(() => error);
                return this.tryRefreshToken(auth, next).pipe(
                  switchMap( _ => this.applyHeaders(auth, next, req))
                )
              }),
              catchError( error => {
                if (!(error instanceof HttpErrorResponse) || error.status != 401) return throwError(() => error);
                auth.logout();
                return throwError(() => error);
              })
            )
          }),
        );
      })
    );
  }
}
